const INACTIVE_RETURN_PATH_KEY = 'inactiveReturnPath';
const INACTIVE_RETURN_PENDING_KEY = 'inactiveReturnPending';
const INACTIVE_APP_STATE = 'inactive';

let playerPlaying = false;

/** Player page reports whether content is actively playing (false when paused/ended/unmounted). */
export function setPlayerPlaying(playing: boolean): void {
  playerPlaying = playing;
}

export function isPlayerPlaying(): boolean {
  return playerPlaying;
}

function isBrowser(): boolean {
  return typeof window !== 'undefined';
}

function normalizePath(path: string): string | null {
  if (!isBrowser()) return null;
  if (!path || !path.startsWith('/') || path.startsWith('//')) return null;

  try {
    const url = new URL(path, window.location.origin);
    if (url.searchParams.get('appState') === INACTIVE_APP_STATE) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function getCurrentRoute(): string | null {
  if (!isBrowser()) return null;
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

export function markInactiveReturnRoute(
  path: string | null,
  options: { wasPlaying?: boolean } = {}
): void {
  if (!isBrowser()) return;

  const normalizedPath = path ? normalizePath(path) : null;
  if (!normalizedPath) {
    clearInactiveReturnRoute();
    return;
  }

  try {
    const url = new URL(normalizedPath, window.location.origin);
    if (url.pathname === '/player') {
      // Only come back paused when playback was paused; otherwise resume playing.
      // Always reset first: the current URL may still carry the flag from a previous
      // inactive round trip, which would force a pause even though it is playing now.
      url.searchParams.delete('resumePausedFromInactive');
      if (options.wasPlaying !== true) {
        url.searchParams.set('resumePausedFromInactive', '1');
      }
    }
    window.sessionStorage.setItem(
      INACTIVE_RETURN_PATH_KEY,
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    window.sessionStorage.setItem(INACTIVE_RETURN_PATH_KEY, normalizedPath);
  }

  window.sessionStorage.setItem(INACTIVE_RETURN_PENDING_KEY, '1');
}

export function consumeInactiveReturnRoute(): string | null {
  if (!isBrowser()) return null;

  const isPending = window.sessionStorage.getItem(INACTIVE_RETURN_PENDING_KEY) === '1';
  if (!isPending) return null;

  const storedPath = window.sessionStorage.getItem(INACTIVE_RETURN_PATH_KEY);
  const normalizedPath = storedPath ? normalizePath(storedPath) : null;

  clearInactiveReturnRoute();
  return normalizedPath;
}

export function clearInactiveReturnRoute(): void {
  if (!isBrowser()) return;

  window.sessionStorage.removeItem(INACTIVE_RETURN_PATH_KEY);
  window.sessionStorage.removeItem(INACTIVE_RETURN_PENDING_KEY);
}
